import React from "react";
import { EvaluationCriteria } from "../../../model_gen/evaluation";
import { Checkbox } from "../../ui/checkbox";
import { PlusIcon, InfoIcon, PencilIcon } from "lucide-react";
import { Button } from "../../ui/button";
import { Tooltip } from "antd";
import { renderCriteriaTooltip } from "./CriteriaTooltip";
import CustomCriteriaForm from "./CustomCriteriaForm";

interface CustomCriteriaSectionProps {
  customCriteria: EvaluationCriteria[];
  selectedCriteriaIds: number[];
  toggleCriteria: (id: number) => void;
  editingCriteriaId: number | null;
  startEditCriteria: (crit: EvaluationCriteria) => void;
  showCustomForm: boolean;
  setShowCustomForm: (v: boolean) => void;
  customName: string;
  setCustomName: (v: string) => void;
  customInstructions: string;
  setCustomInstructions: (v: string) => void;
  customScoringType: "numeric" | "boolean";
  onScoringTypeChange: (type: "numeric" | "boolean") => void;
  loading: boolean;
  onSave: () => void;
  resetCustomForm: () => void;
}

const CustomCriteriaSection: React.FC<CustomCriteriaSectionProps> = ({
  customCriteria,
  selectedCriteriaIds,
  toggleCriteria,
  editingCriteriaId,
  startEditCriteria,
  showCustomForm,
  setShowCustomForm,
  customName,
  setCustomName,
  customInstructions,
  setCustomInstructions,
  customScoringType,
  onScoringTypeChange,
  loading,
  onSave,
  resetCustomForm,
}) => (
  <div className="space-y-3">
    {customCriteria.map((crit) => {
      const isSelected = selectedCriteriaIds.includes(crit.id!);
      if (editingCriteriaId === crit.id) return null;
      return (
        <div
          key={crit.id}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted/50 transition-colors"
        >
          <Checkbox
            id={`custom-${crit.id}`}
            checked={isSelected}
            onCheckedChange={() => toggleCriteria(crit.id!)}
          />
          <label
            htmlFor={`custom-${crit.id}`}
            className="text-sm font-gc-med text-foreground cursor-pointer"
          >
            {crit.name}
          </label>
          {(crit.config as any)?.scoring_type === "boolean" && (
            <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded font-gc-med uppercase">
              Pass / Fail
            </span>
          )}
          <Tooltip
            placement="right"
            title={renderCriteriaTooltip(crit.instructions || "")}
            overlayStyle={{ maxWidth: 680 }}
          >
            <InfoIcon className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
          </Tooltip>
          <div className="flex-1" />
          <button
            onClick={() => startEditCriteria(crit)}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <PencilIcon className="w-3.5 h-3.5" />
          </button>
        </div>
      );
    })}

    {showCustomForm ? (
      <CustomCriteriaForm
        customName={customName}
        setCustomName={setCustomName}
        customInstructions={customInstructions}
        setCustomInstructions={setCustomInstructions}
        customScoringType={customScoringType}
        onScoringTypeChange={onScoringTypeChange}
        editingCriteriaId={editingCriteriaId}
        loading={loading}
        onSave={onSave}
        onCancel={resetCustomForm}
      />
    ) : (
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          resetCustomForm();
          setShowCustomForm(true);
        }}
        className="gap-2"
      >
        <PlusIcon className="w-4 h-4" />
        Add Custom Criterion
      </Button>
    )}
  </div>
);

export default CustomCriteriaSection;
